import { writable, derived, get } from 'svelte/store';
import { startPoll, pollStatus, getStages } from './api';
import { isSupportView } from './supportView';

// Derive the initial view from the URL synchronously at module-load time.
// Without this, currentView would default to 'today' until onMount runs
// initViewFromUrl(), which means a hard refresh of /dashboard (or any
// non-Today path) flashes Today for one frame before the corrected view
// renders. Reading window.location here happens before the first paint
// because the App component imports this store at module init.
const _PAGES = new Set([
  'today', 'dashboard', 'contacts', 'teach',
  'prospecting', 'sequences', 'george', 'settings', 'scheduled', 'calendar',
  'admin', 'super-admin', 'onboarding-preview',
]);
const _URL_SLUG_TO_VIEW: Record<string, string> = { voice: 'george' };
// Retired top-level pages (2026-08-03), now tabs of /teach. Slug -> tab name.
//
// THE ONLY definition — App.svelte imports this rather than keeping its own copy.
// Both need it: App does the real routing, and this module runs at load time, so
// without it a hard refresh of /knowledge paints one frame of ClientView (fetching
// a "client" called knowledge) before App corrects the view.
//
// Nothing in the app emits these URLs any more — verified: no navTo / currentView
// assignment / href references them, and neither does _backend or _pipeline. This
// exists purely so pre-existing bookmarks and back-button history land on the right
// tab instead of silently falling back to Today. Safe to delete outright once those
// are judged dead; deleting it is a one-liner in each consumer.
export const LEGACY_TEACH_TABS: Record<string, string> = {
  knowledge: 'knowledge',
  outreach: 'collateral',
  collateral: 'collateral',
};
function _initialView(): string {
  if (typeof window === 'undefined') return 'today';
  const path = window.location.pathname || '/';
  if (path.startsWith('/clients/')) return path.slice('/clients/'.length);
  const slug = path.slice(1);
  if (LEGACY_TEACH_TABS[slug]) return 'teach';
  const aliased = _URL_SLUG_TO_VIEW[slug];
  if (aliased && _PAGES.has(aliased)) return aliased;
  if (_PAGES.has(slug)) return slug;
  return 'today';
}
export const currentView = writable<string>(_initialView());
// Last top-level page (today / dashboard / contacts / prospecting / etc.)
// the user was on before entering a client view. Read by ClientView's
// back-crumb so it can say "← Back to Today" / "← Back to Dashboard" etc.
// Empty when the SPA cold-loaded directly on a client (deep link).
export const lastTopLevelView = writable<string>('');
export const clients = writable<string[]>([]);
/** The sidebar's client list. Identical to `GET /clients?active_only=true`
 *  (routers/clients.py), so deriving it from `stages` saves a request instead
 *  of giving the list a second source that can disagree. */
export function activeClientsFrom(stages: Record<string, string>): string[] {
  return Object.keys(stages).filter((c) => stages[c] !== 'Non-Clients').sort();
}
export const stages = writable<Record<string, string>>({});
let stageRequest = 0;

/** Only the newest stage read may replace the shared map. A successful write
 * invalidates reads started before it, so a slow response cannot undo a click. */
export async function refreshStages(): Promise<Record<string, string>> {
  const request = ++stageRequest;
  const snapshot = await getStages();
  if (request === stageRequest) {
    stages.set(snapshot);
    clients.set(activeClientsFrom(snapshot));
  }
  return get(stages);
}

export function setStagesSnapshot(snapshot: Record<string, string>): void {
  ++stageRequest;
  stages.set(snapshot);
  clients.set(activeClientsFrom(snapshot));
}

export function setClientStage(client: string, stage: string): void {
  ++stageRequest;
  const snapshot = { ...get(stages) };
  if (stage) snapshot[client] = stage;
  else delete snapshot[client];
  stages.set(snapshot);
  clients.set(activeClientsFrom(snapshot));
}
export const needsOnboarding = writable<boolean>(false);
export const userEmail = writable<string>('');
export const agentName = writable<string>('');
export const agentTitle = writable<string>('');
export const pollVersion = writable<number>(0);
export const georgeUnread = writable<number>(0);
export const userTier = writable<number>(0);
export const userActive = writable<boolean>(true);
export const userDaysRemaining = writable<number | null>(null);
export const subscriptionEndsAt = writable<string>('');  // ISO date if cancel scheduled, '' otherwise
export const isAdmin = writable<boolean>(false);
export const georgeDiscussContext = writable<string>('');
// A hidden prompt auto-sent to a client's chat on load (e.g. the Re-engage
// button). `label` is shown as a small italic header; `prompt` is sent to
// George but never rendered — the user just sees the response generate.
export const chatSeed = writable<{ label: string; prompt: string } | null>(null);

/* Set by a page that navigates INTO a client already knowing WHICH PERSON it means —
   Contacts' per-row `Open`. ClientView consumes it once, opens the People tab and expands that
   contact, then clears it. Without this, arriving from a contact row dumps you on the Emails
   tab with no trace of the person you clicked.
   ⚠️ Carries the client too: ClientView must ignore a seed left over for a different client. */
export const clientContactSeed = writable<{ client: string; email: string } | null>(null);

// When the Respond bucket's "Discuss" button is clicked we route to the
// client view with the auto-drafted reply pre-loaded into the reply composer
// (To, CC split into client/internal/other, Subject, Body, thread_id).
// ClientView's Emails tab picks this up, opens the thread, and populates the
// reply form. Cleared after consumption.
export const replyDraftSeed = writable<{
  client: string;
  threadId: string;
  to: string;
  ccClient: string[];
  ccInternal: string[];
  ccOther: string;
  subject: string;
  body: string;
  // Type-specific instruction the original draft was written under
  // (LIGHT/HEAVY overdue, COLD, MEETING_FOLLOWUP, inbound reply).
  // ClientView uses it as `instruction` when the user clicks Generate
  // from the chat-draft composer, so a regen after Discuss doesn't
  // silently drop the framing into a generic compose. Prospecting tiles
  // bypass this store entirely — they route to the Prospecting tab via
  // `prospectingTileSeed` below.
  brief?: string;
} | null>(null);

// Pre-populated state for the Prospecting tab when the user clicks
// Discuss on a Today prospecting tile. Lets the Prospecting page open
// with the target/contact picked, the existing draft loaded into the
// edit card, and the discuss-chat seeded — instead of routing into
// ClientView (which is for engaged clients, not cold outreach).
export const prospectingTileSeed = writable<{
  client: string;
  contactEmail: string;
  contactName: string;
  subject: string;
  body: string;
  brief: string;
} | null>(null);
// Clients the user has pinned to the sidebar for quick access.
// Populated from GET /dashboard/pinned on session load.
export const pinnedClients = writable<string[]>([]);
export const fundPins = writable<{ counterparty_id?: string; entity_key?: string; entry_contact_id?: string; display_name: string; stage: string | null }[]>([]);
// Every LP the sidebar lists, pinned ones first. Server-ordered (GET /fund/pins)
// so the nav rows and the Dashboard's entity rows stay the same grouping.
export const fundLps = writable<{ counterparty_id?: string; entity_key?: string; entry_contact_id?: string; display_name: string; stage: string | null; pinned: boolean }[]>([]);
// True while a poll/sync subprocess is running. Lets pages that show
// poll-derived data (dashboard rows, contacts) refresh themselves
// iteratively as the pipeline writes rows, instead of waiting for the
// final pollVersion bump.
export const pollingActive = writable<boolean>(false);
export const orgPricing = writable<{ text: number; voice: number }>({ text: 150, voice: 250 });
export const authProvider = writable<'google' | 'microsoft'>('google');

// ── Per-user cached view state ────────────────────────────────────────────
// Pages that stale-while-revalidate park their last payload in localStorage
// (`lib/swr.ts`, plus Today's own two keys) so a return visit paints instantly.
// Those payloads carry REAL customer data — client names, contact email
// addresses, inbound email snippets, drafted reply bodies — and localStorage is
// origin-scoped, not session-scoped. Nothing cleared them on logout, so on a
// shared machine the next person to sign in got the previous user's pipeline
// painted on mount, before the first revalidation swapped it out. Since the
// tenant key is the email domain, that leak can cross *companies*.
//
// This is an ALLOWLIST OF WHAT SURVIVES, deliberately inverted: anything not
// named below is wiped. Listing the keys to *remove* is the wrong failure mode
// — a page cache the list forgets outlives logout. Inverted, a page that adds a
// new cache key gets it cleared for free, and forgetting to update this costs a
// cold repaint, not a data leak. Per-user key suffixes are a second line of
// defence, never the first — do not rely on them in place of this list.
//
// Only device-level display prefs belong here — nothing carrying tenant or user
// content. Every `.split-shell` page passes its own pair of rail keys to
// `createSplitRail` — a new page adding a pair must add it HERE too, or its rail
// geometry is wiped on logout with nothing to indicate why. `selectedPersona` is server-backed (`setActivePersona`) and the
// `george_*` settings all have defaults, so both re-establish after sign-in;
// `george_agent_name` is the tenant's AI partner name and must NOT survive.
const KEEP_ON_LOGOUT = new Set([
  'appTheme', 'colorScheme',          // theme
  'cvRailCollapsed', 'cvRailW',       // client-view rail geometry
  'prRailCollapsed', 'prRailW',       // prospecting rail geometry
  'fundRailCollapsed', 'fundRailW', 'fundMikeMode', // IR contact-detail display prefs
  'fundSidebarCompact',               // Fund navigation density
  'chatMode',                         // chat composer toggle
]);

export function clearCachedUserData(): void {
  // These live in memory, not localStorage. Clear them before another user can
  // mount the shell in the same tab after logout or account switching.
  pinnedClients.set([]);
  fundPins.set([]);
  fundLps.set([]);
  fundSyncFilterSummary.set('');
  if (isSupportView()) return;
  if (typeof localStorage === 'undefined') return;
  try {
    for (const k of Object.keys(localStorage)) {
      if (!KEEP_ON_LOGOUT.has(k)) localStorage.removeItem(k);
    }
  } catch {
    // Private mode / quota / disabled storage — best-effort, same as the writes.
  }
}

// Score colour scheme — fixed to 'classic'. The user-facing toggle was
// removed, so we no longer read the (possibly stale) localStorage value:
// every user gets the classic palette regardless of any old saved choice.
export const colorScheme = writable<'classic' | 'new'>('classic');
if (typeof localStorage !== 'undefined') localStorage.removeItem('colorScheme');

// Theme:
//   themePreference — what the user picked: 'light' | 'dark' | 'auto'. Persisted.
//   appTheme        — resolved value: always 'light' or 'dark'. What components read.
// 'auto' follows the OS via window.matchMedia('(prefers-color-scheme: dark)').
export type AppTheme = 'dark' | 'light' | 'auto';
export type ResolvedTheme = 'dark' | 'light';

function resolvePref(pref: AppTheme): ResolvedTheme {
  if (pref === 'auto') {
    if (typeof window !== 'undefined' && window.matchMedia) {
      return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
    }
    return 'light';
  }
  return pref;
}

const cachedPref = typeof localStorage !== 'undefined'
  ? (localStorage.getItem('appTheme') as AppTheme | null)
  : null;
const initialPref: AppTheme = (cachedPref === 'dark' || cachedPref === 'light' || cachedPref === 'auto')
  ? cachedPref
  : 'auto';

export const themePreference = writable<AppTheme>(initialPref);
export const appTheme = writable<ResolvedTheme>(resolvePref(initialPref));

themePreference.subscribe(pref => {
  if (typeof localStorage !== 'undefined' && !isSupportView()) {
    localStorage.setItem('appTheme', pref);
  }
  appTheme.set(resolvePref(pref));
});

appTheme.subscribe(v => {
  if (typeof document !== 'undefined') {
    document.documentElement.classList.remove('theme-default', 'theme-dark', 'theme-light');
    document.documentElement.classList.add(`theme-${v}`);
  }
});

// When preference is 'auto', track OS changes mid-session.
if (typeof window !== 'undefined' && window.matchMedia) {
  const mq = window.matchMedia('(prefers-color-scheme: dark)');
  const onChange = () => {
    let pref: AppTheme = 'light';
    themePreference.subscribe(v => { pref = v; })();
    // `subscribe(...)()` sets `pref` synchronously, but TS can't see the
    // closure assignment and narrows `pref` back to its 'light' initializer —
    // so the 'auto' comparison looks impossible to it. Widen back to AppTheme.
    if ((pref as AppTheme) === 'auto') appTheme.set(mq.matches ? 'dark' : 'light');
  };
  if (mq.addEventListener) mq.addEventListener('change', onChange);
  else if ((mq as any).addListener) (mq as any).addListener(onChange);
}

// Called after /auth/me — updates local cache from DB (cross-device sync)
export function setThemeFromServer(theme: string) {
  if (theme === 'dark' || theme === 'light' || theme === 'auto') {
    themePreference.set(theme);
  }
}

// Called when user changes theme — saves to both localStorage and DB
export async function saveTheme(theme: AppTheme) {
  themePreference.set(theme);
  try {
    await fetch('/api/auth/theme', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ theme }),
    });
  } catch {}
}

export type ScoreColorSet = {
  strong: string;
  moderate: string;
  early: string;
  cold: string;
  meeting: string;
};

const CLASSIC_COLORS: ScoreColorSet = {
  strong: '#fbbf24',
  moderate: '#22c55e',
  early: '#3b82f6',
  cold: '#9ca3af',
  meeting: '#8b5cf6',
};

const NEW_COLORS: ScoreColorSet = {
  strong: '#22c55e',
  moderate: '#2dd4bf',
  early: '#a78bfa',
  cold: '#9ca3af',
  meeting: '#06b6d4',
};

/* ─── Contact avatar ──────────────────────────────────────────────────────────
   The ONLY avatar palette + initials logic. Both lived byte-identically in `Contacts.svelte`
   and `ClientView.svelte`, hash included. Import these; do not re-declare them in a page.

   The colour is derived from the email so one person keeps the same avatar everywhere —
   which only holds while there is a single palette and a single hash. Changing the palette's
   LENGTH re-shuffles every existing avatar; changing a stop's colour does not.
   ⚠️ White text on the #4ADE80 and #F5B042 stops is ~1.6–1.9:1 — logged in
   `archive/todos/contacts-design.md`, not fixed here. */
const AVATAR_PALETTE = [
  'linear-gradient(135deg, #6EA8F0, #4A7BC8)',
  'linear-gradient(135deg, #B689E0, #8B5FB8)',
  'linear-gradient(135deg, #4ADE80, #22A862)',
  'linear-gradient(135deg, #F5B042, #D89234)',
  'linear-gradient(135deg, #E15D3D, #C94D4D)',
  'linear-gradient(135deg, #5EE2D0, #3FB5A8)',
];

/** Stable avatar background for an email address. Same email → same colour, always. */
export function avatarColor(email: string): string {
  let h = 0;
  for (const ch of (email || '')) h = ((h << 5) - h) + ch.charCodeAt(0) | 0;
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length];
}

/** Two-letter initials: first+last name, else the first two chars of the email local-part. */
export function initialsFor(name: string, email: string): string {
  const n = (name || '').trim();
  if (n) {
    const p = n.split(/\s+/).filter(Boolean);
    return (p.length >= 2 ? p[0][0] + p[p.length - 1][0] : n.slice(0, 2)).toUpperCase();
  }
  return ((email || '?').split('@')[0] || '?').slice(0, 2).toUpperCase();
}

/* ─── Stage colours ───────────────────────────────────────────────────────────
   The ONLY stage→colour map in the app. Import `stageColor()`; do not declare a local copy
   in a page — four of them existed and had already drifted apart (see UPDATES 2026-08-17).

   ⚠️ These are fixed hexes, NOT theme tokens, and two are worth knowing before you touch them:
     · `Client: #4ADE80` is exactly the DARK theme's `--green`, so in light mode this dot
       paints a dark-theme colour (`--green` is #16a34a there).
     · `Active Deal: #E15D3D` is NOT `--accent` (#e8553a) — a near-miss coral that reads as
       the brand without being it, so retuning `--accent` does not move it. It is also the
       §1 violation logged in `archive/todos/teach-george-audit.md` (coral must not encode deal
       status). Both are deliberately left as-is: unifying the copies and changing the
       colours are separate decisions. */
/** Pipeline stages in DISPLAY order: most-advanced first (Client → On Hold),
 *  deliberately the reverse of the backend funnel order. Dashboard and Contacts
 *  each kept their own copy with a comment asking the next person to keep them in
 *  step by hand; they had already drifted (only one listed `Non-Clients`).
 *  `Non-Clients` is NOT here — it is a parking bucket, not a funnel stage, and the
 *  Contacts always groups parked clients; Dashboard shows them only when the
 *  Non-Clients stage filter is selected. Each page appends the stage where needed.
 *  `Near Close` is absent from the backend's `CLIENT_STAGES` (storage.py:58) — that
 *  constant is now referenced only by comments; the live vocabulary is this list. */
export const STAGE_ORDER = ['Client', 'Near Close', 'Active Deal', 'Engaged', 'Top of Funnel', 'Prospect', 'On Hold'];

export const STAGE_COLORS: Record<string, string> = {
  'Client':        '#4ADE80',
  'Near Close':    '#B689E0',
  'Active Deal':   '#E15D3D',
  'Engaged':       '#F5B042',
  'Top of Funnel': '#6EA8F0',
  'Prospect':      '#9CA1AC',
  'On Hold':       '#6B7280',
  'Non-Clients':   '#6B7280',
};

/* ─── Fund / IR relationship stages ───────────────────────────────────────────
   Fund stores the Sales stage keys in its own tables. Only Active Deal and
   Client have LP-specific display labels; every other label stays the same.
   The Fund dashboard and detail rail share this ordered list. */
export const FUND_STAGES: { key: string; label: string; color: string }[] = [
  { key: 'Client',         label: 'Existing LP',       color: STAGE_COLORS['Client'] },
  { key: 'Near Close',     label: 'Near Close',        color: STAGE_COLORS['Near Close'] },
  { key: 'Active Deal',    label: 'Active Diligence', color: STAGE_COLORS['Active Deal'] },
  { key: 'Engaged',        label: 'Engaged',           color: STAGE_COLORS['Engaged'] },
  { key: 'Top of Funnel', label: 'Top of Funnel',     color: STAGE_COLORS['Top of Funnel'] },
  { key: 'Prospect',       label: 'Prospect',          color: STAGE_COLORS['Prospect'] },
  { key: 'On Hold',        label: 'On Hold',           color: STAGE_COLORS['On Hold'] },
  // Keep the Sales storage key for API/database compatibility. In Fund this is
  // a human correction to a permissive LP candidate, not a client lifecycle stage.
  { key: 'Non-Clients',    label: 'Not LP',            color: STAGE_COLORS['Non-Clients'] },
];

/** Label for a Fund stage key. Unset is shown, never guessed into a stage. */
export function fundStageLabel(stage: string | null | undefined): string {
  return FUND_STAGES.find(s => s.key === stage)?.label || 'No stage';  // callers floor an LP at Prospect first
}

/** Dot colour for a Fund stage key. Unknown or unset → the muted-text token. */
export function fundStageColor(stage: string | null | undefined): string {
  return FUND_STAGES.find(s => s.key === stage)?.color || 'var(--text-dim)';
}

/** Dot/pill colour for a client stage. Unknown stage → the muted-text token. */
export function stageColor(stage: string): string {
  return STAGE_COLORS[stage] || 'var(--text-dim, #888)';
}

/* ─── Workflow vocabulary ─────────────────────────────────────────────────────
   Which words this account uses. An IR account renders "Existing LP" where a
   Sales one renders "Client", and a component that hardcodes either is a
   component that lies to half the users.

   Filled at boot from `/api/onboarding/status`, which reads
   `_pipeline/workflow_profile.py` — the same file the poller's prompts read.

   The defaults below are the Sales column, so a component rendered without
   that boot call (a unit test, a future entry point) reads correct Sales words
   instead of `undefined`. They ARE a second copy of those strings, and the
   thing that stops the two drifting is a test, not a convention:
   `_backend/tests/test_fund_user_fork.py` parses this file and asserts it
   matches `workflow_profile.SALES` key for key. If you add a term, add it in
   the Python file first and that test will tell you to come back here.

   Stored values never change. `stageLabel()` maps a stored stage to a label and
   never maps back — the DB, every API body, STAGE_ORDER and STAGE_COLORS above
   all stay in the Sales vocabulary. That is what makes IR a relabel rather than
   a migration. */
const SALES_STAGE_LABELS: Record<string, string> = {
  'Prospect': 'Prospect',
  'Top of Funnel': 'Top of Funnel',
  'Engaged': 'Engaged',
  'Active Deal': 'Active Deal',
  'Client': 'Client',
  'On Hold': 'On Hold',
  'Non-Clients': 'Non-Clients',
  'Near Close': 'Near Close',
};

const SALES_TERMS: Record<string, string> = {
  workspace: '',
  workspace_short: '',
  counterparty: 'client',
  counterparty_plural: 'clients',
  counterparty_title: 'Client',
  counterparty_title_plural: 'Clients',
  nav_prospecting: 'Prospecting',
  nav_pinned: 'Pinned clients',
  pin_hint: 'Pin clients from the dashboard for quick access.',
  engagement_title: 'Deal',
  score_label: 'Deal score',
  prospect: 'prospect',
  prospect_plural: 'prospects',
  non_counterparty_plural: 'Non-Clients',
};

export const workflow = writable<'sales' | 'fund'>('sales');
export const stageLabels = writable<Record<string, string>>({ ...SALES_STAGE_LABELS });
export const terms = writable<Record<string, string>>({ ...SALES_TERMS });

/** Publish the account's vocabulary. Called from the boot path, and by any test
 *  that needs the IR column. A missing key falls back to the Sales word rather
 *  than to `undefined`, so a backend that gains a term before the SPA reads it
 *  degrades to the old label instead of a blank. */
export function setVocabulary(
  vocabulary: { stage_labels?: Record<string, string>; terms?: Record<string, string> } | undefined,
  name: 'sales' | 'fund',
) {
  workflow.set(name);
  stageLabels.set({ ...SALES_STAGE_LABELS, ...(vocabulary?.stage_labels ?? {}) });
  terms.set({ ...SALES_TERMS, ...(vocabulary?.terms ?? {}) });
}

/** Display label for a STORED stage. An unmapped stage renders as itself, so a
 *  stage nobody labelled degrades to the Sales word rather than to a blank. */
export function stageLabelIn(labels: Record<string, string>, stage: string): string {
  return labels[stage] || stage;
}

/** `$stageLabel(stage)` in markup — a store so it re-renders when the
 *  vocabulary arrives, which happens after the first paint of some pages. */
export const stageLabel = derived(stageLabels,
  (labels) => (stage: string) => stageLabelIn(labels, stage));

export function getScoreColors(scheme: 'classic' | 'new'): ScoreColorSet {
  return scheme === 'classic' ? CLASSIC_COLORS : NEW_COLORS;
}

export function scoreColorFn(scheme: 'classic' | 'new') {
  const c = getScoreColors(scheme);
  return (score: number): string => {
    if (score >= 75) return c.strong;
    if (score >= 50) return c.moderate;
    if (score >= 25) return c.early;
    return c.cold;
  };
}

/** Inline style for a `.score-num` badge: banded tint + matching text and border.
 *  0 reads as an empty outline, <25 as a muted chip, everything else takes its band
 *  colour from `scoreColorFn`. Shared so every table shows a score the same way. */
export function scoreBadgeStyle(score: number, scheme: 'classic' | 'new' = 'new'): string {
  if (score === 0) return 'background: transparent; color: var(--text-dim); border: 1px solid var(--border);';
  if (score < 25) return 'background: var(--bg-hover); color: var(--text-muted); border: 1px solid var(--border);';
  const color = scoreColorFn(scheme)(score);
  return `background: ${color}26; color: ${color}; border: 1px solid ${color}4d;`;
}


// ─── Email sync / poll machinery ─────────────────────────────────────────
// Lifted out of App.svelte so the Pipeline (Dashboard) and Today pages can
// both kick off a sync and render its progress. `pollingActive` (above)
// stays the mirror for poll-derived pages that just want to know if a build
// is in flight; the stores below carry the full UI state.
export type PollProgress = {
  phase?: string;
  total: number;
  processed: number;
  clients: number;
  clients_total?: number;
  pages?: number;
  personal_excluded?: number;
  automated_skipped?: number;
};
export const pollProgress = writable<PollProgress | null>(null);
export const fundSyncFilterSummary = writable<string>('');
export const pollDone = writable<boolean>(false);
export const pollError = writable<boolean>(false);
export const pollErrorDetail = writable<string>('');

const POLL_BASE = '/api';

let _pollWatchTimer: ReturnType<typeof setInterval> | null = null;

function _watchPollStatus() {
  if (_pollWatchTimer) return;
  _pollWatchTimer = setInterval(async () => {
    const s = await pollStatus();
    pollProgress.set(s.progress);
    // Refresh dashboard data while sync is running (rolling updates)
    if (get(workflow) === 'sales') {
      try {
        await refreshStages();
        pollVersion.update(v => v + 1);
      } catch {}
    } else {
      pollVersion.update(v => v + 1);
    }
    if (!s.running) {
      if (_pollWatchTimer) { clearInterval(_pollWatchTimer); _pollWatchTimer = null; }
      pollingActive.set(false);
      pollProgress.set(null);
      pollDone.set(true);
      const isErr = s.exit_code !== null && s.exit_code !== 0;
      if (get(workflow) === 'fund' && !isErr && s.progress) {
        const personal = s.progress.personal_excluded || 0;
        const automated = s.progress.automated_skipped || 0;
        fundSyncFilterSummary.set([
          personal ? `${personal} personal address${personal === 1 ? '' : 'es'} excluded by your setting` : '',
          automated ? `${automated} automated email${automated === 1 ? '' : 's'} ignored` : '',
        ].filter(Boolean).join(' · '));
      }
      pollError.set(isErr);
      pollErrorDetail.set('');
      pollVersion.update(v => v + 1);
      if (isErr && s.log_tail) {
        // Email auth expired — force re-login
        if (s.log_tail.includes('GMAIL_AUTH_EXPIRED') || s.log_tail.includes('MICROSOFT_AUTH_EXPIRED')) {
          alert('Email authorization expired. Please sign in again to reconnect.');
          clearCachedUserData();   // before the fetch — see App.svelte:logout
          await fetch(`${POLL_BASE}/auth/logout?clear_gmail=true`, { method: 'POST', credentials: 'include' });
          window.location.reload();
          return;
        }
        // Email rate limit
        const retryMatch = s.log_tail.match(/Retry after (\d{4}-\d{2}-\d{2}T[\d:.]+Z)/);
        if (s.log_tail.includes('rateLimitExceeded') || s.log_tail.includes('rate limit')) {
          const retryTime = retryMatch ? new Date(retryMatch[1]).toLocaleTimeString() : 'a few minutes';
          pollErrorDetail.set(`Email rate limited. Try again after ${retryTime}.`);
        }
        // Anthropic overloaded
        if (s.log_tail.includes('overloaded') || s.log_tail.includes('529')) {
          pollErrorDetail.set('Anthropic temporarily unavailable. Sync will resume automatically.');
        }
      }
      setTimeout(() => { pollDone.set(false); pollErrorDetail.set(''); }, 8000);
    }
  }, 3000);
}

// Start a sync. Safe to call from any page — if a poll is already running
// (daemon or another tab) it just attaches the watcher.
export async function startSync(fullRebuild = false) {
  if (get(pollingActive)) return;
  pollingActive.set(true);
  pollDone.set(false);
  pollError.set(false);
  pollErrorDetail.set('');
  fundSyncFilterSummary.set('');
  try {
    await startPoll(fullRebuild);
  } catch (error) {
    // A refused or failed start is not a successful empty sync. In particular,
    // the Fund safety gate returns 409 until its own repository is connected.
    pollingActive.set(false);
    pollError.set(true);
    pollErrorDetail.set(error instanceof Error ? error.message : 'Could not start mailbox sync.');
    return;
  }
  _watchPollStatus();
}

// On app mount: if a sync is already running, attach the watcher.
export async function checkExistingPoll() {
  try {
    const s = await pollStatus();
    if (s.running) {
      pollingActive.set(true);
      _watchPollStatus();
    }
  } catch {}
}
